import React, { FC, useEffect } from 'react';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { RouteConstants } from 'app/routes';
import { useDispatch, useSelector } from 'react-redux';
import {
  generatePath,
  RouteComponentProps,
  useLocation,
} from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import { Alert, Button, Form, PageHeader, Row } from 'antd';
import MapCard from 'app/containers/Map/parts/MapCard/MapCard';
import {
  LocationStateWithMapBounds,
  MapModeEnum,
} from 'app/containers/Map/slice/types';
import DrawnFieldsList from '../../parts/DrawnFieldsList/DrawnFieldsList';

import * as S from './CreateField.styles';
import { selectSelectedCompanyId } from 'app/containers/Company/slice/selectors';
import { selectSelectedSeasonId } from 'app/containers/Season/slice/selectors';
import { useMapSlice } from 'app/containers/Map/slice';
import { useResponsiveQuery } from 'utils/responsiveQuery/useResponsiveQuery';
import * as L from 'leaflet';
import { useGenerateFieldNames } from '../../hooks/useGenerateFieldNames';

interface IProps extends RouteComponentProps {}

const DRAWN_FIELDS_FORM_ID = 'drawnFieldsForm';

const CreateField: FC<IProps> = ({ history }) => {
  const { t } = useTranslation();

  const [form] = Form.useForm();

  const { generateFieldNames } = useGenerateFieldNames(form);

  const { mobileOrTablet } = useResponsiveQuery();

  const dispatch = useDispatch();

  const { actions: mapActions } = useMapSlice();

  const location = useLocation<LocationStateWithMapBounds>();

  const mapBounds = location.state?.mapBounds
    ? L.latLngBounds(
        location.state.mapBounds.southWest,
        location.state.mapBounds.northEast,
      )
    : undefined;

  const envId = useSelector(selectSelectedEnvId) as number;
  const seasonId = useSelector(selectSelectedSeasonId) as number;
  const companyId = useSelector(selectSelectedCompanyId);

  useEffect(() => {
    if (envId && seasonId) {
      // Load all polygons on map
      dispatch(
        mapActions.onLoad({ envId, seasonId, companyId, companyGroupId: null }),
      );
    }
  }, [mapActions, dispatch, envId, seasonId, companyId]);

  if (!companyId) {
    return (
      <Alert
        message={t(
          'Field.AddField.SelectCompanyToAddField',
          'To add fields please select a company in header.',
        )}
        type="info"
      />
    );
  }

  const responsivePadding = mobileOrTablet ? 6 : 12;

  return (
    <>
      <PageHeader
        onBack={() =>
          history.push(
            generatePath(RouteConstants.field.addField, {
              envId,
            }),
          )
        }
        className="page-header"
        title={t('Field.CreateField.Title', 'Draw field')}
        extra={[
          <Button key="generate-names" onClick={generateFieldNames}>
            {t(
              'Field.TempFieldsListItem.GenerateFieldNames',
              'Generate field names',
            )}
          </Button>,

          <Button
            type="primary"
            key="submit"
            htmlType="submit"
            form={DRAWN_FIELDS_FORM_ID}
          >
            {t('Field.CreateField.SaveBtn', 'Save')}
          </Button>,
        ]}
      />
      <Row
        wrap={mobileOrTablet}
        gutter={responsivePadding}
        style={{
          paddingLeft: responsivePadding,
          paddingTop: responsivePadding,
          width: '100%',
        }}
      >
        <S.LeftCol>
          <DrawnFieldsList form={form} formId={DRAWN_FIELDS_FORM_ID} />
        </S.LeftCol>
        <S.MapCol flex="auto">
          <MapCard
            mode={MapModeEnum.createPolygon}
            readOnly
            showCulturesMenu={false}
            mapContainerProps={{ bounds: mapBounds }}
          />
        </S.MapCol>
      </Row>
    </>
  );
};

export default CreateField;
